import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../../services/api';

/**
 * Qué acciones del agente necesitan visto bueno, y a qué estados puede mover un
 * expediente.
 *
 * Vive en base de datos y no en código a propósito: si algo se porta mal, se le
 * enciende la revisión ese mismo minuto, sin desplegar ni reiniciar nada. Y al
 * revés — si la revisión de algo estorba, se apaga igual de rápido.
 *
 * Las acciones están ordenadas por DAÑO, no por canal: cuánto duele si se
 * equivoca y si se puede deshacer. Es el orden en que tiene sentido ir soltando
 * autonomía, y por eso se muestra así en vez de alfabéticamente.
 */

// De menos a más irreversible. El orden es la recomendación.
const ESCALERA = [
  { type: 'create_annotation', label: 'Crear anotación', nota: 'reversible, no sale de aquí' },
  { type: 'update_expedient_status', label: 'Cambiar estado', nota: 'reversible, interno' },
  { type: 'create_follow_up_assignment', label: 'Crear tarea de seguimiento', nota: 'reversible, interno' },
  { type: 'send_whatsapp', label: 'Enviar WhatsApp', nota: 'NO se deshace: llega a una persona' },
  { type: 'send_whatsapp_template', label: 'Enviar plantilla de WhatsApp', nota: 'NO se deshace: llega a una persona' },
  { type: 'send_email', label: 'Enviar email', nota: 'NO se deshace: llega a una persona' },
  {
    type: 'start_ar_email_sequence',
    label: 'Encender la secuencia de correo (AR)',
    nota: 'NO se deshace, y sigue sola: a partir de aquí los correos salen días',
  },
  { type: 'send_sms', label: 'Enviar SMS', nota: 'NO se deshace: llega a una persona' },
];

/**
 * `basePath` decide de quién es: `/ai-agent` es la global de ClaimUp;
 * `/ar-review` es la de una empresa sobre sus expedientes AR (06-reparto §4.4),
 * que va encima de la global. En la de la empresa se guarda solo lo que ella
 * cambia, para que lo que no toque siga a la global. Ver
 * docs/ar-ia-modo-automatico-plan.md.
 */
/**
 * Los números de un tipo de acción en una línea, para decidir si soltarlo
 * (docs/ar-ia-kpis-supervision-plan.md). Solo en la de la empresa.
 */
const pct = v => (v == null ? '—' : `${Math.round(v * 100)} %`);
export function resumenKpi(k) {
  if (!k || !k.total) return 'Sin propuestas en los últimos 30 días';
  const partes = [`${k.total} propuestas`];
  if (k.revisadas) {
    partes.push(`${pct(k.aprobadas_tal_cual)} aprobadas tal cual`);
    if (k.lo_hago_yo) partes.push(`${pct(k.lo_hago_yo)} «Lo hago yo»`);
    if (k.mediana_revision_ms != null) {
      const h = k.mediana_revision_ms / 3600000;
      partes.push(`se revisan en ${h < 1 ? `${Math.round(h * 60)} min` : `${Math.round(h * 10) / 10} h`}`);
    }
  }
  if (k.automaticas) partes.push(`${k.automaticas} salieron solas`);
  return partes.join(' · ');
}

const AIAgentReviewSettings = ({ onChange, basePath = '/ai-agent' }) => {
  const deEmpresa = basePath !== '/ai-agent';
  const [settings, setSettings] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [kpis, setKpis] = useState({});

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`${basePath}/settings`);
      setSettings(data.data);
      if (deEmpresa) {
        // Si fallan los números, la configuración se ve igual: no son la puerta.
        const r = await api.get(`${basePath}/kpis`).catch(() => null);
        setKpis(Object.fromEntries((r?.data?.data?.por_tipo || []).map(k => [k.type, k])));
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [basePath, deEmpresa]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const alternar = async type => {
    const actual = settings.review_by_action || {};
    const valor = !(actual[type] !== false);
    const nuevo = deEmpresa
      ? { ...(settings.company_overrides || {}), [type]: valor }
      : { ...actual, [type]: valor };
    setGuardando(true);
    try {
      const { data } = await api.put(`${basePath}/settings`, { review_by_action: nuevo });
      setSettings(data.data);
      toast.success(
        valor
          ? 'A partir de ahora esta acción espera visto bueno.'
          : 'A partir de ahora esta acción se ejecuta sola.'
      );
      if (onChange) onChange();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!settings) return null;
  // La empresa decide; su gestor lo ve pero no lo cambia.
  const soloLectura = deEmpresa && settings.can_edit === false;

  const revisar = settings.review_by_action || {};
  // Un tipo que no está en el mapa se revisa: es lo que hace el backend y la UI
  // no debe decir lo contrario.
  const seRevisa = type => revisar[type] !== false;

  // Acciones que existen en la configuración pero no en la escalera de arriba:
  // alguien añadió una herramienta y no se actualizó esta lista.
  const extras = Object.keys(revisar).filter(t => !ESCALERA.some(e => e.type === t));

  return (
    <div className='bg-white rounded-lg border border-slate-200 p-4 mb-6'>
      <p className='text-sm font-semibold text-slate-800 mb-1'>Qué se revisa</p>
      {deEmpresa && settings.agent_paused && (
        <p className='mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800'>
          ClaimUp ha parado el agente en vuestros expedientes AR
          {settings.paused_reason ? `: ${settings.paused_reason}` : ''}. No propone ni envía nada hasta que lo reanude.
        </p>
      )}
      <p className='text-xs text-slate-500 mb-4'>
        {deEmpresa
          ? 'En vuestros expedientes AR. Lo que se ejecuta sola sale sin pasar por vosotros. Ordenado por cuánto duele si el agente se equivoca; el efecto es inmediato.'
          : 'Ordenado por cuánto duele si el agente se equivoca. Se cambia sin desplegar: el efecto es inmediato.'}
        {soloLectura && ' Solo la empresa lo puede cambiar.'}
      </p>

      <div className='space-y-2'>
        {[...ESCALERA, ...extras.map(t => ({ type: t, label: t, nota: 'sin clasificar' }))].map(
          (a, i) => (
            <div
              key={a.type}
              className='flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0'
            >
              <div className='min-w-0'>
                <p className='text-sm text-slate-800'>
                  <span className='text-slate-400 mr-2'>{i + 1}</span>
                  {a.label}
                </p>
                <p className='text-xs text-slate-500'>{a.nota}</p>
                {deEmpresa && <p className='text-xs text-slate-600 mt-0.5'>{resumenKpi(kpis[a.type])}</p>}
                {deEmpresa && kpis[a.type]?.sugerir_automatico && (
                  <p className='text-xs font-medium text-emerald-700 mt-0.5'>
                    Casi siempre la aprobáis tal cual: podría salir sola.
                  </p>
                )}
              </div>
              <button
                type='button'
                disabled={guardando || soloLectura}
                onClick={() => alternar(a.type)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-50 ${
                  seRevisa(a.type)
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-slate-50 border-slate-200 text-slate-600'
                }`}
              >
                {seRevisa(a.type) ? 'Espera visto bueno' : 'Se ejecuta sola'}
              </button>
            </div>
          )
        )}
      </div>

      {!deEmpresa && (
      <div className='mt-4 pt-3 border-t border-slate-100'>
        <p className='text-xs font-semibold text-slate-500 uppercase mb-1'>
          Estados a los que puede mover un expediente
        </p>
        <p className='text-xs text-slate-600'>{(settings.allowed_statuses || []).join(' · ')}</p>
      </div>
      )}
    </div>
  );
};

export default AIAgentReviewSettings;
